import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, CalendarClock, Handshake, SearchCheck, ShieldCheck, Truck, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import { DEALER, SERVICES } from "@/data/dealer";
import { useTitle } from "@/lib/site";
import { PageHeader, CutImage } from "@/components/site/Shared";
import { Reveal } from "@/components/motion/Reveal";

const ICONS = { sourced: SearchCheck, warranty: ShieldCheck, finance: Handshake, video: Video, delivery: Truck, hours: CalendarClock };

export default function About() {
  useTitle("About Us | YK Motors, Peterborough");
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "About us" }]}
        eyebrow="About YK Motors"
        title={["Family-run.", "Peterborough-based."]}
        intro={`An independent used car dealer at ${DEALER.address.line1}, ${DEALER.address.town} — rated ${DEALER.rating.score} on AutoTrader from ${DEALER.rating.reviews} customer reviews.`}
      />

      <section className="py-16 lg:py-28" aria-labelledby="about-story">
        <div className="container-x grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-6 lg:order-2">
            <CutImage src="/stock/202608255419756/00.webp" alt="Ford Focus on the YK Motors forecourt" className="aspect-[4/3] w-full" />
          </Reveal>
          <Reveal className="lg:col-span-6 lg:order-1" delay={0.1}>
            <p className="eyebrow">Who we are</p>
            <h2 id="about-story" className="mt-4 font-display text-3xl font-extrabold leading-[1.04] tracking-tight sm:text-4xl lg:text-[2.75rem]">
              Privately sourced. Properly checked.
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-steel md:text-lg">
              <p>We're a family-run business with over 20 years in the motor trade.</p>
              <p>
                We pride ourselves on privately sourcing our vehicles, and each one is thoroughly quality checked by our staff as well as qualified
                mechanics.
              </p>
              <p>For your peace of mind, most vehicles come with a free Gold cover warranty, with the option to extend it if you wish.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper py-16 lg:py-24" aria-labelledby="about-services">
        <div className="container-x">
          <Reveal className="mb-10 max-w-2xl lg:mb-14">
            <p className="eyebrow">Buying with us</p>
            <h2 id="about-services" className="mt-4 font-display text-3xl font-extrabold leading-[1.04] tracking-tight sm:text-4xl">
              Straightforward from first call to collection.
            </h2>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.key];
              const dark = i === 0;
              return (
                <Reveal key={s.key} delay={(i % 3) * 0.08} className="h-full">
                  <div className={cn("flex h-full flex-col rounded-lg border p-7", dark ? "on-dark border-charcoal bg-charcoal text-white" : "border-line bg-white")} data-testid={`service-tile-${s.key}`}>
                    <Icon className={cn("h-7 w-7", dark ? "text-brand-light" : "text-brand")} aria-hidden="true" />
                    <h3 className="mt-8 font-display text-xl font-extrabold leading-tight">{s.title}</h3>
                    <p className={cn("mt-3 text-[15px] leading-relaxed", dark ? "text-zinc-300" : "text-steel")}>{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-8 text-sm text-steel">Please confirm current services with the team before you buy.</p>
        </div>
      </section>

      <section className="on-dark bg-charcoal py-16 text-white lg:py-24" aria-labelledby="about-awards">
        <div className="container-x grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Recognition</p>
            <h2 id="about-awards" className="mt-4 font-display text-3xl font-extrabold leading-[1.04] tracking-tight sm:text-4xl">
              AutoTrader Highly Rated, three years running.
            </h2>
            <p className="mt-5 leading-relaxed text-zinc-300">
              Highly Rated awards are given by AutoTrader to sellers who receive consistently high review ratings from customers over a 12-month
              period.
            </p>
          </Reveal>
          <Reveal className="grid grid-cols-3 gap-3 lg:col-span-7" delay={0.1}>
            {DEALER.awards.map((y) => (
              <div key={y} className="rounded-lg border border-white/15 p-5 sm:p-7">
                <BadgeCheck className="h-6 w-6 text-brand-light" aria-hidden="true" />
                <p className="mt-8 font-display text-3xl font-extrabold sm:text-5xl">{y}</p>
                <p className="mt-2 text-xs text-zinc-300 sm:text-sm">Highly Rated</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <Reveal className="container-x flex flex-col items-start justify-between gap-8 border-y border-line py-12 md:flex-row md:items-center">
          <h2 className="max-w-xl font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">Ready to take a closer look?</h2>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/used-cars" className="btn btn-primary" data-testid="about-browse-stock-button">
              Browse our cars <ArrowRight className="arrow" aria-hidden="true" />
            </Link>
            <Link to="/contact" className="btn btn-outline" data-testid="about-contact-button">
              Get in touch
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
